import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/lib/translations";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "cookie_consent_v1";
export const OPEN_COOKIE_SETTINGS_EVENT = "open-cookie-settings";

type ConsentChoice = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: 1;
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const applyConsent = (analytics: boolean, marketing: boolean) => {
  const w = window;
  if (typeof w.gtag === "function") {
    w.gtag("consent", "update", {
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
      analytics_storage: analytics ? "granted" : "denied",
    });
  }
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "consent_update",
    consent_analytics: analytics,
    consent_marketing: marketing,
  });
};

const save = (analytics: boolean, marketing: boolean) => {
  const choice: ConsentChoice = {
    necessary: true,
    analytics,
    marketing,
    timestamp: new Date().toISOString(),
    version: 1,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
  } catch {
    // ignore storage errors (private mode etc.)
  }
  applyConsent(analytics, marketing);
};

const CookieConsent = () => {
  const { t, isRTL } = useTranslations();
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    if (!stored) {
      setOpen(true);
    } else {
      try {
        const parsed = JSON.parse(stored);
        setAnalytics(!!parsed.analytics);
        setMarketing(!!parsed.marketing);
      } catch {
        setOpen(true);
      }
    }

    const handler = () => {
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
  }, []);

  const acceptAll = () => {
    setAnalytics(true);
    setMarketing(true);
    save(true, true);
    setOpen(false);
    setShowDetails(false);
  };

  const rejectAll = () => {
    setAnalytics(false);
    setMarketing(false);
    save(false, false);
    setOpen(false);
    setShowDetails(false);
  };

  const savePreferences = () => {
    save(analytics, marketing);
    setOpen(false);
    setShowDetails(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6"
      dir={isRTL ? "rtl" : "ltr"}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-white dark:bg-background border border-border rounded-lg shadow-2xl p-5 sm:p-6">
        <div className="flex gap-3 items-start mb-3">
          <Cookie className="h-6 w-6 text-se-green shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2
              id="cookie-consent-title"
              className="font-semibold text-base text-se-navy dark:text-foreground mb-1"
            >
              {t("cookie.title")}
            </h2>
            <p className="text-sm text-muted-foreground">{t("cookie.description")}</p>
          </div>
        </div>

        {showDetails && (
          <div className="space-y-3 my-4 border-t border-border pt-4">
            <label className="flex items-start gap-3 cursor-not-allowed opacity-80">
              <input
                type="checkbox"
                checked
                disabled
                className="mt-1"
                data-testid="checkbox-necessary"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{t("cookie.necessary")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("cookie.necessaryDesc")}
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-1"
                data-testid="checkbox-analytics"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{t("cookie.analytics")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("cookie.analyticsDesc")}
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-1"
                data-testid="checkbox-marketing"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{t("cookie.marketing")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("cookie.marketingDesc")}
                </div>
              </div>
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          {!showDetails && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetails(true)}
              data-testid="button-cookie-customize"
            >
              {t("cookie.customize")}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={rejectAll}
            data-testid="button-cookie-reject"
          >
            {t("cookie.rejectAll")}
          </Button>
          {showDetails && (
            <Button
              variant="outline"
              size="sm"
              onClick={savePreferences}
              data-testid="button-cookie-save"
            >
              {t("cookie.savePreferences")}
            </Button>
          )}
          <Button
            variant="accent"
            size="sm"
            onClick={acceptAll}
            data-testid="button-cookie-accept"
          >
            {t("cookie.acceptAll")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
